import React from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Cell, flexRender, Row } from '@tanstack/react-table';

import { cn } from '../../lib/utils';
import { TableCell, TableRow } from '../../ui';
import { SIZE_CLASSES } from '../DataTable.consts';
import { IDataTableRowProps, Size } from '../DataTable.types';
import {
  getCellWidth,
  getDragHandleTestId,
  getRowTestId
} from '../DataTable.utils';

function renderCellContent<T extends object>(
  cell: Cell<T, unknown>
): React.ReactNode {
  return (
    <div
      className={cn(
        'inline-flex items-center justify-start w-full',
        'whitespace-nowrap overflow-hidden text-ellipsis max-w-full',
        '[&::-webkit-scrollbar]:hidden'
      )}
      style={{
        minWidth: 0,
        color: 'var(--base-sidebar-foreground)'
      }}
    >
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </div>
  );
}

interface IDataTableRowComponentProps<T extends object>
  extends IDataTableRowProps<T> {
  dragHandleColumnId?: string;
}

export const DataTableRow = <T extends object>({
  row,
  size,
  rowIdIdentifier,
  onRowClick,
  enableColumnResizing,
  dragHandleColumnId
}: IDataTableRowComponentProps<T>): React.ReactElement => {
  const isSortable = Boolean(dragHandleColumnId);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: row.id, disabled: !isSortable });

  const rowTestId = getRowTestId(row, rowIdIdentifier);
  const dragHandleTestId = getDragHandleTestId(row, rowIdIdentifier);

  const handleRowClick = (): void => {
    if (onRowClick) {
      onRowClick(row.original);
    }
  };

  const getRowClassName = (): string => {
    const classNames = [
      'first:border-t-0 box-border',
      'hover:bg-base-muted',
      SIZE_CLASSES[size as Size]
    ];

    if (onRowClick) {
      classNames.push('cursor-pointer');
    }

    if (isSortable && isDragging) {
      classNames.push('opacity-50 bg-base-muted z-10');
    }

    return cn(...classNames);
  };

  const getRowStyle = (): React.CSSProperties => {
    if (!isSortable) {
      return {};
    }

    return {
      transform: CSS.Transform.toString(transform),
      transition
    };
  };

  const renderDragHandle = (cell: Cell<T, unknown>): React.ReactNode => {
    return (
      <div
        className="inline-flex items-center gap-1.5 w-full"
        {...attributes}
        {...listeners}
        data-testid={dragHandleTestId}
      >
        <FontAwesomeIcon
          icon="fa-solid fa-grip-vertical"
          className="text-base-mutedForeground text-sm shrink-0"
        />
        {renderCellContent(cell)}
      </div>
    );
  };

  const getCellClassName = (cell: Cell<T, unknown>): string => {
    const isDragHandleColumn =
      isSortable && cell.column.id === dragHandleColumnId;

    const classNames = [
      'px-4 border-t text-left text-base-foreground text-p-s',
      'last:text-right'
    ];

    if (!enableColumnResizing) {
      classNames.push('min-w-0 max-w-0');
    }

    if (isDragHandleColumn) {
      classNames.push('pl-0 cursor-grab active:cursor-grabbing');
    }

    return cn(...classNames);
  };

  const renderCellInner = (cell: Cell<T, unknown>): React.ReactNode => {
    const isDragHandleColumn =
      isSortable && cell.column.id === dragHandleColumnId;

    if (isDragHandleColumn) {
      return renderDragHandle(cell);
    }

    return renderCellContent(cell);
  };

  const renderCell = (cell: Cell<T, unknown>): React.ReactNode => {
    return (
      <TableCell
        data-testid={cell.column.id}
        key={cell.id}
        className={getCellClassName(cell)}
        style={{
          overflow: 'hidden',
          width: getCellWidth(cell, enableColumnResizing)
        }}
      >
        {renderCellInner(cell)}
      </TableCell>
    );
  };

  const getRowRef = (): ((node: HTMLElement | null) => void) | undefined => {
    if (isSortable) {
      return setNodeRef;
    }
    return undefined;
  };

  return (
    <TableRow
      ref={getRowRef()}
      style={getRowStyle()}
      data-testid={rowTestId}
      className={getRowClassName()}
      onClick={handleRowClick}
    >
      {row.getVisibleCells().map((cell: Cell<T, unknown>) => renderCell(cell))}
    </TableRow>
  );
};
